'use client'

import * as React from 'react'

import { submitDDEvent } from '@/lib/observability/datadog-rum'

type ShaderIslandProps = {
  children: React.ReactNode
}

let hasReported = false

/**
 * Decorative shaders are lost for the whole page load once WebGL is
 * unavailable, so one non-error RUM action keeps the signal visible without
 * inflating error counts (which is what the crash used to do).
 */
function reportShaderUnavailableOnce(
  reason: 'webgl-unsupported' | 'mount-failed',
  error?: unknown
): void {
  if (hasReported) {
    return
  }
  hasReported = true
  submitDDEvent('decorative_shader_unavailable', {
    reason,
    message: error instanceof Error ? error.message : undefined,
  })
}

/**
 * `@paper-design/shaders` throws "WebGL is not supported in this browser"
 * straight from its context creation, and it does so inside an async effect
 * where a React boundary can never see it. Probe first instead. The probe
 * context is released immediately because browsers cap how many live WebGL
 * contexts a single page may hold.
 */
function supportsWebGl2(): boolean {
  try {
    const gl = document.createElement('canvas').getContext('webgl2')
    if (!gl) {
      return false
    }
    const loseContext = gl.getExtension('WEBGL_lose_context') as {
      loseContext: () => void
    } | null
    loseContext?.loseContext()
    return true
  } catch {
    return false
  }
}

class ShaderErrorBoundary extends React.Component<
  ShaderIslandProps,
  { hasFailed: boolean }
> {
  state = { hasFailed: false }

  static getDerivedStateFromError(): { hasFailed: boolean } {
    return { hasFailed: true }
  }

  componentDidCatch(error: unknown): void {
    reportShaderUnavailableOnce('mount-failed', error)
  }

  render(): React.ReactNode {
    return this.state.hasFailed ? null : this.props.children
  }
}

/**
 * Containment wrapper for the decorative dither shaders. The animation is
 * `aria-hidden` and `pointer-events-none`, so it must never take a route
 * down with it: without WebGL2, or if the mount throws for any other reason,
 * the island renders nothing and the host surface stays intact.
 *
 * The probe needs the DOM and rendering the shader server-side would desync
 * hydration, so the island stays empty until the first client effect.
 */
export function ShaderIsland({ children }: ShaderIslandProps) {
  const [isSupported, setIsSupported] = React.useState(false)

  React.useEffect(() => {
    const canRenderShader = supportsWebGl2()
    if (!canRenderShader) {
      reportShaderUnavailableOnce('webgl-unsupported')
    }
    setIsSupported(canRenderShader)
  }, [])

  if (!isSupported) {
    return null
  }
  return <ShaderErrorBoundary>{children}</ShaderErrorBoundary>
}
